import { useMemo, useState } from "react";
import { Trash2, UserPlus, Save, X, Loader2, ChevronRight, ChevronDown } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiError } from "../lib/apiClient";
import { useCustomers } from "../context/CustomerContext";
import { TAB_CATALOG, ALL_TAB_IDS, LEGACY_OPEN_TABS, PORTAL_VISIBLE_TABS, cellVisible, type TabId, type PermissionMatrix } from "../lib/permissionsCatalog";
import { USERS_QUERY_KEY } from "./UsersAdminTab.queries";

// Role tiers — see lib/db/src/schema/users.ts and api-server userStore.ts
// for the authoritative definitions. SOC L1 / L2 are scoped to specific
// customers via `allowedCustomerIds`, identical to the legacy 'analyst' role.
// L1 vs L2 is enforced at the UI/permission layer for elevated tabs.
type Role = "admin" | "developer" | "software_tester" | "soc_manager" | "soc_l1" | "soc_l2" | "analyst" | "customer_viewer";

// Hand-written label/help copy for the built-in roles. Kept as a lookup
// (not the full option list) so the wording stays consistent, while the
// actual selectable role list now comes from Masters -> Role Master via
// useRoleOptions() below -- any role added there becomes invitable without
// a code change. Custom roles fall back to their own Role Master
// description for the help text.
const KNOWN_ROLE_META: Record<string, { label: string; help: string; legacy?: boolean }> = {
  admin:            { label: "Admin",            help: "All customers + manages SOC users, audit log, tenant credentials. Sees MSSP Overview." },
  developer:        { label: "Developer", help: "Platform developer. Full admin access plus Masters module for role, permission, table, and report governance." },
  software_tester:  { label: "Software Tester", help: "Full validation access across modules; delete/destructive actions are blocked by backend policy." },
  soc_manager:      { label: "SOC Manager",      help: "Sees every operational tab EXCEPT MSSP Overview. Cannot open Dashboard Settings. Per-customer permissions configurable below." },
  soc_l2:           { label: "SOC L2 (senior)",  help: "Full investigation + Playbooks, SLA, Suppression. Per-customer permissions configurable below." },
  soc_l1:           { label: "SOC L1 (junior)",  help: "Investigation + view-only access to Playbook, SLA & Suppression configuration. Per-customer permissions configurable below." },
  analyst:          { label: "Analyst (legacy)", help: "Equivalent to SOC L2. Kept for existing accounts.", legacy: true },
  customer_viewer:  { label: "Customer Portal",  help: "Read-only single-tenant view for the customer themselves." },
};

interface RoleOption { value: string; label: string; help: string; legacy?: boolean }

/** Live role list from Masters -> Role Master, merged with the hand-written
 *  copy above for the built-in roles. Shared across components via
 *  react-query's cache, so calling this in multiple places doesn't cause
 *  duplicate network requests. */
function useRoleOptions(): RoleOption[] {
  const query = useQuery({
    queryKey: ["masters-role-branding"],
    queryFn: async () => {
      const data = await apiFetch<{ roles: { id: string; label: string; description?: string; color?: string }[] }>("/masters/role-branding");
      return data.roles;
    },
  });

  return useMemo(() => {
    const fetched = query.data ?? [];
    if (fetched.length === 0) {
      // Still loading (or the fetch failed) -- fall back to the built-in
      // list so the form remains usable rather than showing an empty select.
      return Object.entries(KNOWN_ROLE_META).map(([value, meta]) => ({ value, ...meta }));
    }
    return fetched.map((r) => {
      const known = KNOWN_ROLE_META[r.id];
      return {
        value: r.id,
        label: known?.label ?? r.label,
        help: known?.help ?? r.description ?? "Custom role configured in Masters → Role Master.",
        legacy: known?.legacy,
      };
    });
  }, [query.data]);
}

/** Scoped roles use `allowedCustomerIds` (the customer-checkbox grid).
 *  Exclude-list (not allow-list) so a new custom role from Role Master
 *  gets the customer-assignment UI by default, matching the backend's
 *  isSocStaffRole() default. */
function isScopedRole(r: Role): boolean {
  return r !== "admin" && r !== "developer" && r !== "customer_viewer";
}

function roleLabel(r: Role): string {
  return KNOWN_ROLE_META[r]?.label ?? r;
}

function roleBadgeClasses(r: Role): string {
  switch (r) {
    case "admin":           return "bg-violet-500/15 text-violet-700 dark:text-violet-300";
    case "developer": return "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300";
    case "software_tester": return "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300";
    case "soc_manager":     return "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300";
    case "soc_l2":          return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
    case "soc_l1":          return "bg-blue-500/15 text-blue-700 dark:text-blue-300";
    case "analyst":         return "bg-blue-500/15 text-blue-700 dark:text-blue-300";
    case "customer_viewer": return "bg-amber-500/15 text-amber-700 dark:text-amber-300";
    default:                return "bg-slate-500/15 text-slate-700 dark:text-slate-300";
  }
}

interface ManagedUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  allowedCustomerIds: string[];
  scopedCustomerId?: string | null;
  permissions?: PermissionMatrix;
  createdAt: string;
  lastLoginAt?: string;
}

type CustomerLite = { id: string; name: string; color?: string };

// ─── Matrix editor (shared by invite + edit) ────────────────────────────────

/**
 * Renders the per-customer × per-tab × {Read, Write} permission matrix.
 *
 * The matrix is keyed by the user's `allowedCustomerIds`. For each allowed
 * customer the admin can:
 *   - expand a row to see the tab grid
 *   - check Read / Write per tab
 *   - "Full access" / "Read-only" / "No access" quick presets per customer
 *
 * If a customer has NO entry in the matrix, the user falls back to legacy
 * behavior on that customer (full read+write). This is shown explicitly in
 * the UI so the admin understands the default.
 */
function PermissionsMatrixEditor({
  allowedCustomerIds,
  customers,
  value,
  onChange,
  isPortal,
}: {
  allowedCustomerIds: string[];
  customers: CustomerLite[];
  value: PermissionMatrix;
  onChange: (next: PermissionMatrix) => void;
  /**
   * True when editing a customer_viewer (portal) user. Portal users are
   * server-side hard-blocked from writes, and the legacy fallback for
   * `LEGACY_OPEN_TABS` cells is `visible+read` (vs full V+R+W for SOC
   * staff). The editor uses this to mirror runtime semantics so the
   * displayed checkboxes match what the user actually gets.
   */
  isPortal: boolean;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Portal users only render a subset of tabs (see PORTAL_TABS in
  // CustomerPortal.tsx). Restricting the matrix to that subset stops
  // admins from toggling tabs the portal user can never see anyway.
  const visibleCatalog = useMemo(
    () => (isPortal ? TAB_CATALOG.filter((t) => PORTAL_VISIBLE_TABS.has(t.id)) : TAB_CATALOG),
    [isPortal],
  );
  const visibleTabIds = useMemo(
    () => (isPortal ? ALL_TAB_IDS.filter((id) => PORTAL_VISIBLE_TABS.has(id)) : ALL_TAB_IDS),
    [isPortal],
  );

  if (allowedCustomerIds.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground italic">
        Tick at least one customer above to configure per-tab read/write permissions.
      </p>
    );
  }

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function setCell(customerId: string, tab: TabId, patch: Partial<{ visible: boolean; read: boolean; write: boolean }>) {
    const next: PermissionMatrix = { ...value };
    const tabs = { ...(next[customerId] ?? {}) };
    const current = tabs[tab] ?? { visible: true, read: true, write: true };
    const merged = { ...current, ...patch };
    // Cascade: write ⇒ read ⇒ visible. Up the chain we promote, down the
    // chain we demote — un-ticking Visible kills Read+Write, un-ticking Read
    // kills Write.
    if (merged.write) { merged.read = true; merged.visible = true; }
    if (merged.read) merged.visible = true;
    if (merged.visible === false) { merged.read = false; merged.write = false; }
    tabs[tab] = merged;
    next[customerId] = tabs;
    onChange(next);
  }

  function applyPreset(customerId: string, preset: "full" | "read" | "none" | "legacy") {
    const next: PermissionMatrix = { ...value };
    if (preset === "legacy") {
      // Drop the entry → legacy fallback (full V+R+W) kicks in at runtime.
      delete next[customerId];
      onChange(next);
      return;
    }
    const tabs: Record<string, { visible: boolean; read: boolean; write: boolean }> = {};
    // Iterate the visible catalog so portal presets only stamp portal-relevant
    // tabs; otherwise we'd persist meaningless cells for tabs the portal user
    // can never see.
    for (const tab of visibleTabIds) {
      // Portal users can never write (server hard-block), so even the "full"
      // preset tops out at visible+read for them. Keeps stored state honest
      // — no stale write=true cells lingering for portal users.
      if (preset === "full") tabs[tab] = { visible: true, read: true, write: !isPortal };
      else if (preset === "read") tabs[tab] = { visible: true, read: true, write: false };
      else tabs[tab] = { visible: false, read: false, write: false };
    }
    next[customerId] = tabs;
    onChange(next);
  }

  function summary(customerId: string): { label: string; tone: "ok" | "warn" | "muted" } {
    const tabs = value[customerId];
    if (!tabs || Object.keys(tabs).length === 0) {
      return {
        label: isPortal ? "Default (visible + read on every tab)" : "Default (full visible+read+write)",
        tone: "muted",
      };
    }
    let visibles = 0;
    let reads = 0;
    let writes = 0;
    // Iterate only the tabs the user can actually render, so summary totals
    // ("Read on every tab") match what's shown in the table below.
    for (const tab of visibleTabIds) {
      const c = tabs[tab];
      if (!c) {
        // Mirror runtime fallback: legacy tabs without an explicit cell
        // count toward the user's effective access.
        if (LEGACY_OPEN_TABS.has(tab)) {
          visibles += 1;
          reads += 1;
          if (!isPortal) writes += 1;
        }
        continue;
      }
      // Mirror cellVisible() — pre-`visible` cells are visible iff R or W.
      const v = typeof c.visible === "boolean" ? c.visible : (c.read || c.write);
      if (v) visibles += 1;
      if (v && (c.read || c.write)) reads += 1;
      // Portal users are server-blocked from writes, so even if a stale
      // cell carries write=true we never claim the user has write access.
      if (v && c.write && !isPortal) writes += 1;
    }
    const total = visibleTabIds.length;
    if (visibles === 0) return { label: "No access", tone: "warn" };
    if (reads === 0) return { label: `Visible-only on ${visibles} tab${visibles === 1 ? "" : "s"}`, tone: "warn" };
    // Portal users have no write column — collapse summary to the read count.
    if (isPortal) {
      return reads === total
        ? { label: "Read on every tab", tone: "ok" }
        : { label: `Read on ${reads} tab${reads === 1 ? "" : "s"}`, tone: "ok" };
    }
    if (writes === 0) return { label: `Read-only on ${reads} tab${reads === 1 ? "" : "s"}`, tone: "warn" };
    if (writes === total) return { label: "Full read+write", tone: "ok" };
    return { label: `${reads} read · ${writes} write`, tone: "ok" };
  }

  return (
    <div className="space-y-1.5 border border-border rounded-md bg-background/40 p-2">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-1">
        Per-customer access
      </p>
      {allowedCustomerIds.map((cid) => {
        const c = customers.find((x) => x.id === cid);
        const isOpen = expanded.has(cid);
        const sum = summary(cid);
        return (
          <div key={cid} className="border border-border rounded bg-background">
            <button
              type="button"
              onClick={() => toggleExpanded(cid)}
              className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-left hover:bg-muted/40 rounded"
            >
              <div className="flex items-center gap-2 min-w-0">
                {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c?.color ?? "#6b7280" }} />
                <span className="text-xs font-medium text-foreground truncate">{c?.name ?? cid}</span>
              </div>
              <span
                className={
                  "text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap " +
                  (sum.tone === "warn"
                    ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                    : sum.tone === "muted"
                      ? "bg-muted text-muted-foreground"
                      : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300")
                }
              >
                {sum.label}
              </span>
            </button>
            {isOpen && (
              <div className="border-t border-border p-2 space-y-2">
                <div className="flex flex-wrap gap-1">
                  {/* "Full access" is redundant for portal users (their max
                      is read-only), so we hide it to avoid implying writes
                      can be granted. */}
                  {!isPortal && (
                    <button type="button" onClick={() => applyPreset(cid, "full")} className="text-[10px] border border-border px-1.5 py-0.5 rounded hover:bg-muted">Full access</button>
                  )}
                  <button type="button" onClick={() => applyPreset(cid, "read")} className="text-[10px] border border-border px-1.5 py-0.5 rounded hover:bg-muted">{isPortal ? "All visible" : "Read-only"}</button>
                  <button type="button" onClick={() => applyPreset(cid, "none")} className="text-[10px] border border-border px-1.5 py-0.5 rounded hover:bg-muted">No access</button>
                  <button type="button" onClick={() => applyPreset(cid, "legacy")} className="text-[10px] border border-border px-1.5 py-0.5 rounded hover:bg-muted" title="Drop the matrix entry - user falls back to default (full read+write).">Use default</button>
                </div>
                {/* table-fixed + explicit narrow checkbox columns keeps the
                    layout stable now that the matrix carries 12 rows
                    (some labels — "Threat Intelligence",
                    "AI Assistant" — are long and would otherwise
                    push checkbox columns off-card at narrow widths). The
                    label cell wraps softly so nothing gets clipped. */}
                <table className="w-full text-[11px] table-fixed">
                  <colgroup>
                    <col />
                    <col className="w-10" />
                    <col className="w-10" />
                    {!isPortal && <col className="w-10" />}
                  </colgroup>
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium pb-1">Tab</th>
                      <th className="font-medium pb-1 text-center" title="Show this tab in the user's nav">Vis</th>
                      <th className="font-medium pb-1 text-center">R</th>
                      {/* Portal users are server-blocked from writes — hide
                          the column entirely so the matrix only exposes
                          toggles that have a real effect. */}
                      {!isPortal && <th className="font-medium pb-1 text-center">W</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCatalog.map((tab) => {
                      const cell = value[cid]?.[tab.id];
                      const hasEntry = !!value[cid] && Object.keys(value[cid]).length > 0;
                      // Display rules — mirror the runtime resolver so the
                      // checkboxes match what the user actually gets:
                      //  • No matrix entry at all  → legacy default (full
                      //    V+R+W for SOC, V+R for portal). Shows the admin
                      //    what they'd be replacing once they tick anything.
                      //  • Entry exists, cell missing, tab in
                      //    LEGACY_OPEN_TABS → same legacy default. Without
                      //    this, tabs added after the original 7-tab matrix
                      //    shipped (mssp/triage/overview/datalake/logs) would
                      //    appear "denied" in the editor while the runtime
                      //    fallback silently grants access — confusing.
                      //  • Entry exists, cell missing, original 7 tab → off
                      //    (explicit deny, matches runtime).
                      //  • Cell present → render its values.
                      const legacyFallback = !hasEntry || LEGACY_OPEN_TABS.has(tab.id);
                      const fallbackV = legacyFallback;
                      const fallbackR = legacyFallback;
                      const fallbackW = legacyFallback && !isPortal;
                      const cellVisible = cell
                        ? (typeof cell.visible === "boolean" ? cell.visible : (cell.read || cell.write))
                        : fallbackV;
                      const read = cell ? cellVisible && (cell.read || cell.write) : fallbackR;
                      const write = cell ? cellVisible && cell.write : fallbackW;
                      return (
                        <tr key={tab.id} className="border-t border-border/50">
                          <td className="py-1 pr-2 align-middle">
                            <span className="text-foreground break-words leading-tight block" title={tab.hint}>{tab.label}</span>
                          </td>
                          <td className="py-1 text-center">
                            <input
                              type="checkbox"
                              checked={cellVisible}
                              onChange={(e) => setCell(cid, tab.id, { visible: e.target.checked })}
                              title="Show this tab in the user's nav. Unticking also clears Read and Write."
                            />
                          </td>
                          <td className="py-1 text-center">
                            <input
                              type="checkbox"
                              checked={read}
                              onChange={(e) => setCell(cid, tab.id, { read: e.target.checked, write: e.target.checked ? write : false })}
                            />
                          </td>
                          {!isPortal && (
                            <td className="py-1 text-center">
                              <input
                                type="checkbox"
                                checked={write}
                                onChange={(e) => setCell(cid, tab.id, { write: e.target.checked })}
                              />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main panel ─────────────────────────────────────────────────────────────

export function UsersAdminTab({ currentUserId }: { currentUserId: string }) {
  const { customers } = useCustomers();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [inviteNotice, setInviteNotice] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  // Default to soc_l1 (most common new hire). The legacy 'analyst' option
  // is hidden from the dropdown, so it must NOT be the default — otherwise
  // the controlled select renders with an unselected value and submit would
  // silently create a legacy-tier user.
  const roleOptions = useRoleOptions();
  const [inviteRole, setInviteRole] = useState<Role>("soc_l1");
  const [inviteCustomerIds, setInviteCustomerIds] = useState<string[]>([]);
  const [inviteScopedCustomerId, setInviteScopedCustomerId] = useState<string>("");
  const [invitePerms, setInvitePerms] = useState<PermissionMatrix>({});

  // ── Read: user list ───────────────────────────────────────────────────────
  // React Query handles caching, dedupe, background refetch, and gives us a
  // stable reference for the user list so other components rendering the
  // same data don't trigger duplicate /admin/users calls.
  const usersQuery = useQuery({
    queryKey: USERS_QUERY_KEY,
    queryFn: async () => {
      const data = await apiFetch<{ users: ManagedUser[] }>("/admin/users");
      return data.users;
    },
  });
  const users: ManagedUser[] = usersQuery.data ?? [];
  const loading = usersQuery.isLoading;
  const queryError = usersQuery.error
    ? (usersQuery.error instanceof ApiError ? usersQuery.error.message : String(usersQuery.error))
    : null;

  function resetInvite(): void {
    setInviteEmail("");
    setInviteName("");
    setInviteRole("soc_l1");
    setInviteCustomerIds([]);
    setInviteScopedCustomerId("");
    setInvitePerms({});
    setShowInvite(false);
  }

  // ── Write: invite / update / delete ───────────────────────────────────────
  // Each mutation invalidates USERS_QUERY_KEY on success so the table
  // re-reads from the server. We keep error state local so transient
  // mutation failures can be surfaced inline without nuking the cached list.
  const inviteMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      apiFetch<{ user: ManagedUser; inviteEmail?: { status: "sent" | "skipped" | "failed"; message: string } }>("/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      const invite = data.inviteEmail;
      if (invite?.status === "sent") setInviteNotice("User created and branded invite email sent successfully.");
      else if (invite?.status === "failed") setInviteNotice(`User created, but invite email failed: ${invite.message}`);
      else if (invite?.status === "skipped") setInviteNotice(`User created. Invite email skipped: ${invite.message}`);
      else setInviteNotice("User created.");
      resetInvite();
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      apiFetch(`/admin/users/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      setEditingId(null);
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiFetch(`/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY }); },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  function submitInvite(): void {
    setError(null);
    setInviteNotice(null);
    if (!inviteEmail.trim()) { setError("Email is required"); return; }
    if (inviteRole === "customer_viewer" && !inviteScopedCustomerId) {
      setError("Customer portal users must be scoped to exactly one customer");
      return;
    }
    inviteMutation.mutate({
      email: inviteEmail.trim(),
      name: inviteName.trim() || undefined,
      role: inviteRole,
      allowedCustomerIds: isScopedRole(inviteRole) ? inviteCustomerIds : [],
      scopedCustomerId: inviteRole === "customer_viewer" ? inviteScopedCustomerId : null,
      // customer_viewer's matrix is keyed by its scopedCustomerId only.
      // For analyst-style scoped roles we ship the full matrix keyed by allowedCustomerIds.
      permissions: isScopedRole(inviteRole)
        ? invitePerms
        : inviteRole === "customer_viewer" && inviteScopedCustomerId && invitePerms[inviteScopedCustomerId]
          ? { [inviteScopedCustomerId]: invitePerms[inviteScopedCustomerId] }
          : {},
      sendInviteEmail: true,
    });
  }

  function saveUser(
    u: ManagedUser,
    patch: Partial<Pick<ManagedUser, "role" | "allowedCustomerIds" | "name" | "permissions" | "scopedCustomerId">>,
  ): void {
    setError(null);
    updateMutation.mutate({ id: u.id, patch: patch as Record<string, unknown> });
  }

  function deleteUser(u: ManagedUser): void {
    if (!confirm(`Remove ${u.email}? They will lose access immediately.`)) return;
    setError(null);
    deleteMutation.mutate(u.id);
  }

  function toggleCustomerInList(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  }

  const submitting = inviteMutation.isPending;
  const displayError = error ?? queryError;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">SOC Users</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Invite SOC analysts and configure per-customer × per-tab read/write permissions. Admins always see everything.
          </p>
        </div>
        {!showInvite && (
          <button
            onClick={() => setShowInvite(true)}
            className="flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 rounded-md text-xs font-semibold hover:opacity-90"
          >
            <UserPlus size={13} /> Invite user
          </button>
        )}
      </div>

      {displayError && (
        <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-700 dark:text-red-300">
          {displayError}
        </div>
      )}

      {inviteNotice && (
        <div className="px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-700 dark:text-blue-300">
          {inviteNotice}
        </div>
      )}

      {showInvite && (
        <div className="border border-border rounded-lg p-4 bg-muted/30 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Email *</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="analyst@kloudynet.com"
                className="w-full mt-1 px-2 py-1.5 text-sm border border-border rounded bg-background"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Display name</label>
              <input
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Optional. Pulled from Microsoft if blank."
                className="w-full mt-1 px-2 py-1.5 text-sm border border-border rounded bg-background"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as Role)}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-border rounded bg-background"
              >
                {roleOptions.filter((o) => !o.legacy).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">
                {roleOptions.find((o) => o.value === inviteRole)?.help}
              </p>
            </div>
          </div>
          {inviteRole === "customer_viewer" && (
            <>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Scoped to customer *</label>
                <select
                  value={inviteScopedCustomerId}
                  onChange={(e) => {
                    const next = e.target.value;
                    setInviteScopedCustomerId(next);
                    // Drop matrix entries for any other customer so we don't
                    // ship dangling perms when the admin re-targets the user.
                    setInvitePerms((prev) => (next && prev[next] ? { [next]: prev[next] } : {}));
                  }}
                  className="w-full mt-1 px-2 py-1.5 text-sm border border-border rounded bg-background"
                >
                  <option value="">Select a customer</option>
                  {customers.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
                <p className="text-[10px] text-muted-foreground mt-1">Portal users see only their assigned customer's incidents and SLA metrics. They cannot mutate state - write toggles below are ignored server-side, but read toggles let you hide tabs from the customer.</p>
              </div>
              {inviteScopedCustomerId && (
                <PermissionsMatrixEditor
                  allowedCustomerIds={[inviteScopedCustomerId]}
                  customers={customers}
                  value={invitePerms}
                  onChange={setInvitePerms}
                  isPortal
                />
              )}
            </>
          )}
          {isScopedRole(inviteRole) && (
            <>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Allowed customers</label>
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-48 overflow-auto border border-border rounded p-2 bg-background">
                  {customers.length === 0 && (
                    <p className="text-xs text-muted-foreground italic col-span-full">
                      No customers configured yet. Add one in the Customers tab first.
                    </p>
                  )}
                  {customers.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-xs text-foreground py-0.5">
                      <input
                        type="checkbox"
                        checked={inviteCustomerIds.includes(c.id)}
                        onChange={() => {
                          setInviteCustomerIds((prev) => toggleCustomerInList(prev, c.id));
                          // If the customer is being removed, drop its matrix
                          // entry so we don't ship dangling perms to the API.
                          if (inviteCustomerIds.includes(c.id) && invitePerms[c.id]) {
                            const { [c.id]: _drop, ...rest } = invitePerms;
                            setInvitePerms(rest);
                          }
                        }}
                      />
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color ?? "#6b7280" }} />
                      {c.name}
                    </label>
                  ))}
                </div>
              </div>
              <PermissionsMatrixEditor
                allowedCustomerIds={inviteCustomerIds}
                customers={customers}
                value={invitePerms}
                onChange={setInvitePerms}
                isPortal={false}
              />
            </>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={resetInvite}
              className="px-3 py-1.5 text-xs font-medium border border-border rounded hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={submitInvite}
              disabled={submitting}
              className="px-3 py-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-60 flex items-center gap-1.5"
            >
              {submitting && <Loader2 size={12} className="animate-spin" />}
              Send invite
            </button>
          </div>
        </div>
      )}

      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-muted/50">
            <tr className="text-left text-xs text-muted-foreground">
              <th className="px-3 py-2 font-semibold">User</th>
              <th className="px-3 py-2 font-semibold">Role</th>
              <th className="px-3 py-2 font-semibold">Customers / Permissions</th>
              <th className="px-3 py-2 font-semibold">Last login</th>
              <th className="px-3 py-2 font-semibold w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-muted-foreground">
                <Loader2 size={14} className="animate-spin inline mr-2" /> Loading users…
              </td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-muted-foreground">
                No users yet.
              </td></tr>
            )}
            {users.map((u) => {
              const isEditing = editingId === u.id;
              const isSelf = u.id === currentUserId;
              return (
                <tr key={u.id} className="border-t border-border align-top">
                  <td className="px-3 py-2">
                    <div className="text-xs font-medium text-foreground">{u.name}</div>
                    <div className="text-[11px] text-muted-foreground">{u.email}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded whitespace-nowrap ${roleBadgeClasses(u.role)}`}>
                      {roleLabel(u.role)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-foreground">
                    {isEditing ? (
                      <UserEditor
                        user={u}
                        customers={customers}
                        onSave={(patch) => saveUser(u, patch)}
                        onCancel={() => setEditingId(null)}
                      />
                    ) : (
                      <ReadOnlyCustomerSummary user={u} customers={customers} />
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-muted-foreground whitespace-nowrap w-32">
                    {u.lastLoginAt
                      ? new Date(u.lastLoginAt).toLocaleString(undefined, {
                          year: "numeric", month: "short", day: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })
                      : "Never"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      {!isEditing && (
                        <button
                          onClick={() => setEditingId(u.id)}
                          className="text-xs text-primary hover:underline"
                        >
                          Edit
                        </button>
                      )}
                      {!isSelf && (
                        <button
                          onClick={() => deleteUser(u)}
                          className="ml-1 p-1 text-muted-foreground hover:text-red-500"
                          title="Remove user"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                      {isSelf && (
                        <span className="ml-1 text-[10px] text-muted-foreground italic">you</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReadOnlyCustomerSummary({ user, customers }: { user: ManagedUser; customers: CustomerLite[] }) {
  if (user.role === "admin" || user.role === "developer") {
  return <span className="text-muted-foreground italic">all customers</span>;
}
  if (user.role === "customer_viewer") {
    const c = customers.find((x) => x.id === user.scopedCustomerId);
    return (
      <span className="inline-flex items-center gap-1 text-[11px] bg-muted px-1.5 py-0.5 rounded">
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c?.color ?? "#6b7280" }} />
        {c?.name ?? user.scopedCustomerId} · read-only
      </span>
    );
  }
  if (user.allowedCustomerIds.length === 0) {
    return <span className="text-orange-600 dark:text-orange-400">no customers assigned</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {user.allowedCustomerIds.map((id) => {
        const c = customers.find((x) => x.id === id);
        const tabs = user.permissions?.[id];
        let badge: { label: string; tone: "ok" | "warn" | "muted" } = {
          label: "default",
          tone: "muted",
        };
        if (tabs && Object.keys(tabs).length > 0) {
          // Mirror the same effective-cell logic the server uses (visible
          // gates read which gates write, with backcompat for pre-`visible`
          // cells via cellVisible()) so the badge can never disagree with
          // what the user will actually see in their nav.
          let visibles = 0;
          let reads = 0;
          let writes = 0;
          for (const tab of ALL_TAB_IDS) {
            const cell = tabs[tab];
            if (!cell) continue;
            const v = cellVisible(cell);
            const r = v && (cell.read || cell.write);
            const w = r && cell.write;
            if (v) visibles += 1;
            if (r) reads += 1;
            if (w) writes += 1;
          }
          if (visibles === 0) badge = { label: "no access", tone: "warn" };
          else if (reads === 0) badge = { label: `${visibles} visible`, tone: "warn" };
          else if (writes === 0) badge = { label: `${reads} read`, tone: "warn" };
          else if (writes === ALL_TAB_IDS.length) badge = { label: "full", tone: "ok" };
          else badge = { label: `${reads}r ${writes}w`, tone: "ok" };
        }
        const toneCls =
          badge.tone === "warn"
            ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
            : badge.tone === "muted"
              ? "bg-muted text-muted-foreground"
              : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
        return (
          <span key={id} className="inline-flex items-center gap-1 text-[11px] bg-muted px-1.5 py-0.5 rounded">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c?.color ?? "#6b7280" }} />
            {c?.name ?? id}
            <span className={`ml-1 text-[9px] font-semibold uppercase px-1 py-px rounded ${toneCls}`}>{badge.label}</span>
          </span>
        );
      })}
    </div>
  );
}

function UserEditor({
  user,
  customers,
  onSave,
  onCancel,
}: {
  user: ManagedUser;
  customers: CustomerLite[];
  onSave: (patch: Partial<Pick<ManagedUser, "role" | "allowedCustomerIds" | "permissions" | "scopedCustomerId">>) => void;
  onCancel: () => void;
}) {
  const roleOptions = useRoleOptions();
  const [role, setRole] = useState<Role>(user.role);
  const [ids, setIds] = useState<string[]>(user.allowedCustomerIds);
  const [scopedCustomerId, setScopedCustomerId] = useState<string>(user.scopedCustomerId ?? "");
  const [perms, setPerms] = useState<PermissionMatrix>(() => user.permissions ?? {});

  // Drop matrix entries for customers that are no longer in the allowed list.
  const cleanPerms = useMemo<PermissionMatrix>(() => {
    const out: PermissionMatrix = {};
    for (const id of ids) {
      if (perms[id]) out[id] = perms[id];
    }
    return out;
  }, [ids, perms]);

  // For customer_viewer the matrix is keyed only by scopedCustomerId.
  const portalPerms = useMemo<PermissionMatrix>(() => {
    if (!scopedCustomerId) return {};
    return perms[scopedCustomerId] ? { [scopedCustomerId]: perms[scopedCustomerId] } : {};
  }, [scopedCustomerId, perms]);

  function toggle(id: string) {
    setIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  const isPortal = role === "customer_viewer";
  const canSave = isPortal ? !!scopedCustomerId : true;

  return (
    <div className="space-y-2 min-w-[18rem]">
      <select
        value={role}
        onChange={(e) => setRole(e.target.value as Role)}
        className="text-xs border border-border rounded px-1 py-0.5 bg-background"
      >
        {roleOptions.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {isPortal && (
        <>
          <div>
            <label className="text-[11px] text-muted-foreground block mb-1">Scoped to customer *</label>
            <select
              value={scopedCustomerId}
              onChange={(e) => {
                const next = e.target.value;
                setScopedCustomerId(next);
                // Drop matrix entries for any other customer so we don't ship
                // dangling perms when the admin re-targets the user.
                setPerms((prev) => (next && prev[next] ? { [next]: prev[next] } : {}));
              }}
              className="w-full text-xs border border-border rounded px-1.5 py-1 bg-background"
            >
              <option value="">Select a customer</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="text-[10px] text-muted-foreground mt-1">
              Portal users see only their assigned customer. Write toggles below are ignored server-side; read toggles let you hide tabs from the customer.
            </p>
          </div>
          {scopedCustomerId && (
            <PermissionsMatrixEditor
              allowedCustomerIds={[scopedCustomerId]}
              customers={customers}
              value={portalPerms}
              onChange={setPerms}
              isPortal
            />
          )}
        </>
      )}
      {isScopedRole(role) && (
        <>
          <div className="flex flex-wrap gap-1">
            {customers.map((c) => (
              <label key={c.id} className="flex items-center gap-1 text-[11px] bg-muted px-1.5 py-0.5 rounded cursor-pointer">
                <input type="checkbox" checked={ids.includes(c.id)} onChange={() => toggle(c.id)} />
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.color ?? "#6b7280" }} />
                {c.name}
              </label>
            ))}
          </div>
          <PermissionsMatrixEditor
            allowedCustomerIds={ids}
            customers={customers}
            value={cleanPerms}
            onChange={setPerms}
            isPortal={false}
          />
        </>
      )}
      <div className="flex gap-1 pt-1">
        <button
          disabled={!canSave}
          onClick={() => onSave({
            role,
            allowedCustomerIds: isScopedRole(role) ? ids : [],
            scopedCustomerId: isPortal ? scopedCustomerId : null,
            permissions: isPortal
              ? portalPerms
              : isScopedRole(role)
                ? cleanPerms
                : {},
          })}
          className="text-[11px] bg-primary text-primary-foreground px-2 py-0.5 rounded flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={10} /> Save
        </button>
        <button onClick={onCancel} className="text-[11px] border border-border px-2 py-0.5 rounded flex items-center gap-1">
          <X size={10} /> Cancel
        </button>
      </div>
    </div>
  );
}
